<template>
    <div class="CategoryFilterItemSlide">
        <div class="RangeSliderBase">
            <input aria-label="minmax" type="range" :step="rangeStep" v-model.number="min" :min="getMinimum()" :max="getMaximum()"
                   @change="changeValue({ type: data.nameMin, value: min })" @input="handleSlide('min')"
                   @mouseup="changeValue({ type: data.nameMin, value: min })">
            <input aria-label="minmax" type="range" :step="rangeStep" v-model.number="max" :min="getMinimum()" :max="getMaximum()"
                   @change="changeValue({ type: data.nameMax, value: max })" @input="handleSlide('max')"
                   @mouseup="changeValue({ type: data.nameMax, value: max })">
            <span :style="rangeIndicatorStyle"></span>
        </div>
        <div class="CategoryFilterItemSlide-inputs">
            <input aria-label="min" type="number" :name="data.nameMin" v-model.number.lazy="min" :min="getMinimum()"
                   @change="this.handleInputChange">
            <span>-</span>
            <input aria-label="max" type="number" :name="data.nameMax" v-model.number.lazy="max" :max="getMaximum()"
                   @change="this.handleInputChange">
            <div class="CategoryFilterItemSlide-unit">{{ data.unit }}</div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref } from "vue";
import { useStore } from "vuex";
import { FilterSlideIdentifier } from "@/types";

export default defineComponent({
    name: "FilterSlide",
    props: {
        data: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const min: Ref<number> = ref(props.data.minValue),
            max: Ref<number> = ref(props.data.maxValue);

        const getMinimum = (): number => {
            return Math.min(props.data.min, props.data.minValue);
        }

        const getMaximum = (): number => {
            return Math.max(props.data.max, props.data.maxValue);
        }

        const store = useStore();
        const changeValue = (slideValue: FilterSlideIdentifier) => store.dispatch('CHANGE_SLIDE_VALUE', slideValue);

        const handleInputChange = (e: Event): void => {
            const input = e.target as HTMLInputElement;
            if (!input) return;

            if (input.hasAttribute("min")) {
                if (input.valueAsNumber < parseInt(input.min))
                    min.value = parseInt(input.min);

                if (input.valueAsNumber > max.value)
                    min.value = max.value;

                if (min.value !== input.valueAsNumber)
                    input.value = min.value.toString();

                changeValue({ type: props.data.nameMin, value: min.value });
            }

            if (input.hasAttribute("max")) {
                if (input.valueAsNumber > parseInt(input.max))
                    max.value = parseInt(input.max);

                if (input.valueAsNumber < min.value)
                    max.value = min.value;

                if (max.value !== input.valueAsNumber)
                    input.value = max.value.toString();

                changeValue({ type: props.data.nameMax, value: max.value });
            }
        }

        const handleSlide = (type: "min" | "max"): void => {
            if (min.value <= max.value)
                return;

            if (type === "min") {
                min.value = max.value;
            } else {
                max.value = min.value;
            }
        }

        store.subscribe((mutation) => {
            if (mutation.type !== 'CLEAR_FILTRATION' && mutation.type !== 'SET_DATA')
                return;

            setTimeout((): void => {
                min.value = props.data.minParam !== 0 ? props.data.minParam : props.data.min;
                max.value = props.data.maxParam !== 0 ? props.data.maxParam : props.data.max;
            });
        });

        return {
            min,
            max,
            getMinimum,
            getMaximum,
            changeValue,
            handleInputChange,
            handleSlide
        }
    },
    computed: {
        rangeStep() {
            return this.getMaximum() - this.getMinimum() > 10 ? 10 : 1;
        },
        rangeIndicatorStyle() {
            const diff = this.getMaximum() - this.getMinimum();

            return {
                width: Math.min(Math.abs((this.min - this.max) / diff * 100), 100) + '%',
                left: Math.abs((this.min - this.getMinimum()) / diff * 100) + '%'
            }
        }
    }
})
</script>